import { css, cx } from "@emotion/css";
import React, { useContext, useRef, useState } from "react";
import { useEventListener } from "usehooks-ts";

import { ButtonGroup, GhostIconButton, useLayout } from "@certn/cascadia-react";

import { NavigationContext } from "../../../context/NavigationProvider";
import { mq, useBoxShadowBorder } from "../../utils";

interface Props {
  children: React.ReactNode;
  toolbar: React.ReactNode;
  isCollapsable?: boolean;
  width?: "standard" | "wide";
}

export function NavigationSidebar(props: Props) {
  const { width = "standard" } = props;

  const ref = useRef(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const boxShadowBottomBorder = useBoxShadowBorder({ type: "bottom" });
  const { isCollapsed, setCollapsed } = useContext(NavigationContext);

  const { toggleMenu } = useLayout();

  useEventListener("scroll", (e) => {
    const target = e.currentTarget as Window;

    setIsScrolling(target.scrollY > 0);
  });

  const handlePress = () => {
    setCollapsed(!isCollapsed);
  };

  const navWidth =
    width === "standard" ? styles.standardWidth : styles.wideWidth;

  return (
    <nav
      ref={ref}
      className={css(styles.container, navWidth)}
      data-collapsed={isCollapsed}
    >
      {props.isCollapsable && (
        <div className={css(styles.collapserContainer)}>
          <div className={cx("collapser", styles.collapser)}>
            <GhostIconButton
              onPress={handlePress}
              hasDropShadow
              icon="keyboard_double_arrow_left"
              shape="border-radius-half"
              fill="background-active"
              hoverFill="background-active"
              size="small"
            />
          </div>
        </div>
      )}
      <header
        className={css(
          styles.header,
          isScrolling ? boxShadowBottomBorder : undefined
        )}
      >
        <ButtonGroup>{props.toolbar}</ButtonGroup>

        <GhostIconButton
          icon="close"
          onPress={() => toggleMenu(false)}
          testID="navigation-sidebar-close-button"
          aria-label="Close navigation sidebar"
        />
      </header>
      <section className={cx("children", styles.children)}>
        {props.children}
      </section>
    </nav>
  );
}

const styles = {
  standardWidth: css({
    width: 240,
  }),
  wideWidth: css({
    width: 320,
  }),
  collapserContainer: css({
    position: "relative",
    width: "0px",
    height: "0px",
    marginLeft: "auto",
  }),
  collapser: css(
    {
      position: "absolute",
      left: "-15px",
      top: "92px",

      i: { transition: "transform 200ms ease" },
      transition: "opacity 200ms ease",
    },
    mq({
      display: ["none", "none", "flex"],
    })
  ),
  container: css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    transition: "width 200ms ease",

    ".collapser": {
      opacity: 0,
    },

    "&:hover .collapser": {
      opacity: 1,
    },

    "&[data-collapsed=true]": {
      ".collapser i": {
        transform: "rotate(180deg) !important",
      },

      width: 64,
    },
  }),
  header: css(
    {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      height: "64px",
      paddingLeft: "var(--space-4)",
      paddingRight: "var(--space-4)",
    },
    mq({
      display: ["flex", "flex", "none"],
    })
  ),
  children: css({
    flex: 1,
    height: "100%",
    paddingTop: "var(--space-4)",
    paddingBottom: "var(--space-4)",
    gap: "var(--space-3)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",

    "&::-webkit-scrollbar": {
      width: 3,
    },
    "&::-webkit-scrollbar-track": {
      opacity: 0,
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#D9D9D9",
      borderRadius: 24,
    },
  }),
};
